import { useState, useEffect } from "react";
import LogoImage from "common/components/logo-image";
import NavBar from "common/components/dashboard/navbar";
import ProfileLink from "common/components/dashboard/profile-link";
import Breadcrumbs from "common/components/breadcrumbs";
import { TabGroup } from 'common/components/tab';
import { FaNoteSticky } from 'react-icons/fa6';
import Button from "common/components/button";
import { ButtonGroup, Tag } from '@chakra-ui/react';

const DashboardLayout = ({
  children,  
  admin = false,
  partner = false,
  links,
  user,
  onLogoutClick,
  title,
  buttons,
  tabs,
  sidebar,
  showAlert = false,
}) => {
  const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isLeftMenuOpen, setLeftMenuOpen] = useState(false);
  const [isDesktopSidebarOpen, setDesktopSidebarOpen] = useState(false);

  useEffect(() => {
    if (isMobileSidebarOpen || isLeftMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMobileSidebarOpen, isLeftMenuOpen]);

  return (
    <main className="h-screen flex mobile:flex-col overflow-hidden">
      {/* aside */}
      <aside className="h-full w-full max-w-[240px] flex flex-col relative gap-6 px-4 py-8 border-r border-gray-200 mobile:hidden">
        {/* icon */}
        <div className={`flex flex-col w-fit items-end gap-1`}>
          <LogoImage className="ml-4 mobile:ml-3" size="s" />
          {admin && (
            <span className="text-sm font-medium text-gray-500">
              Admin Portal
            </span>
          )}
          {partner && (
            <span className="text-sm font-medium text-gray-500">
              Partner Portal
            </span>
          )}
        </div>

        {/* link buttons */}
        <div className="flex flex-col gap-3">{links}</div>

        {/* profile details and logout */}
        <ProfileLink user={user} onClick={onLogoutClick} />
      </aside>

      {/* mobile: hamburger menu */}
      <NavBar
        links={links}
        user={user}
        onLogoutClick={onLogoutClick}
        hasRightSidebar={sidebar}
        isMenuOpen={isLeftMenuOpen}
        onToggleMenu={() => setLeftMenuOpen(!isLeftMenuOpen)}
        onRightSidebarOpen={() => setMobileSidebarOpen(true)}
        showAlert={showAlert}
      />

      <div className="flex flex-1 min-w-0 h-full pb-32 lg:pb-0">
        {/* main section */}
        <section className="flex-1 relative flex flex-col overflow-y-auto">
          {title ? (
            <div className="px-4 py-3 sm:px-6 sm:py-4 lg:p-8 flex w-full gap-5 sm:sticky top-0 bg-white border-b border-gray-200 z-20 flex-col items-start">
              <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center w-full">
                <div className="flex flex-col gap-2">
                  {/* breadcrumb */}
                  <Breadcrumbs />

                  {/* title */}
                  <h1 className="text-3xl font-display tracking-tight">{title}</h1>
                </div>
                <div className='fixed bottom-0 inset-x-0 flex justify-end gap-2 bg-white border-t border-gray-200 py-4 px-6 lg:static lg:items-center lg:border-none lg:p-0 ' >
                  {sidebar && (
                    <ButtonGroup className='hidden lg:flex relative mr-2'>
                      <Button
                        onClick={() => setDesktopSidebarOpen(!isDesktopSidebarOpen)}
                        hideTitle
                        theme='secondary'
                        Icon={FaNoteSticky}
                        size='iconOnly'
                      />
                      {showAlert && (
                        <Tag 
                          className='rounded-full p-0 w-4 h-4 text-xs font-bold absolute -top-2 -right-2 items-center justify-center'
                          colorScheme='red' 
                          size='sm'
                        >
                          !
                        </Tag>
                      )}
                    </ButtonGroup>
                  )}
                  {buttons}                  
                </div>              
              </div>
              {tabs ? (
                <div className="flex flex-row gap-2 -ml-4 sm:-ml-6 lg:-ml-8">
                  <TabGroup 
                    tabs={tabs}
                    className='px-4 sm:px-6 lg:px-8' 
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Content */}
          <div className="p-4 sm:p-6 lg:p-8 flex flex-col w-full gap-8 h-full">
            {children}
          </div>
        </section>

        {/* Desktop sidebar */}
        {sidebar && isDesktopSidebarOpen && (
          <aside className="h-full w-full max-w-sm flex-shrink-0 border-l border-gray-200 p-6 hidden lg:block overflow-y-auto">
            {sidebar}
          </aside>
        )}
      </div>

      {/* Mobile sidebar */}
      {sidebar && (
        <>
          <div className={`fixed inset-0 bg-black bg-opacity-25 z-30 transition-opacity lg:hidden ${isMobileSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setMobileSidebarOpen(false)} aria-hidden="true" />
          <aside className={`border-l border-gray-200 fixed top-0 right-0 h-full w-full max-w-sm bg-white z-40 transform transition-transform ease-in-out duration-300 lg:hidden ${isMobileSidebarOpen ? 'translate-x-0' : 'translate-x-full'}`}>
            <div className="p-6 overflow-y-auto h-full">
              {sidebar}
            </div>
          </aside>
        </>
      )}
    </main>
  );
};

export default DashboardLayout;
